import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type BubbleNode, groupPosition, groupRisk, groupTint, type MapData, setRiskMaturity, tokenMaturity, TRADER_TAG_INFO } from '@pleco/shared';

import { BRAND } from '../../brand.js';
import { BubbleCanvas, type CanvasApi, type MapOverlay } from '../BubbleCanvas.js';
import { IconChart, IconClose, IconCopy, IconDownload, IconFit, IconInfo, IconLink, IconList, IconMap, IconMinus, IconPlus, IconStar, IconUsers, IconBolt } from '../Icons.js';
import { fmtPct, fmtPrice, fmtUsd, shortAddr } from '../../lib/fmt.js';
import { MOBILE_QUERY, useMedia } from '../../lib/useMedia.js';
import { useResizable } from '../../lib/useResizable.js';
import { useLiveFeed } from '../../lib/liveFeed.js';
import { LiveAlerts } from './LiveAlerts.js';
import { buildVerdict, LEVEL_LABEL } from '../../lib/verdict.js';
import { useMapStore } from '../../store/mapStore.js';
import { groupById, useTerminal, type LiveLink } from '../../store/terminalStore.js';
import { WalletDrawer } from '../terminal/WalletDrawer.js';
import { GroupList, HolderList, TradeList, VerdictCard, groupTitle, pct } from './Panels.js';

// The chart library is ~180 KB: only load it when someone asks for the chart.
const PriceChart = lazy(() => import('../terminal/PriceChart.js').then((m) => ({ default: m.PriceChart })));

type PanelTab = 'groups' | 'holders' | 'trades';
type MobileView = 'map' | PanelTab;

/**
 * Token page. One idea: the bubble map, with a verdict that explains it.
 *   desktop  [token bar ─────────────────────────────────────────]
 *            [ map (+ optional chart)        │ verdict / tabs     ]
 *   mobile   token bar · one full-height view · bottom tab bar
 */
export function TokenView({ data, onShare, onWatch }: { data: MapData; onShare: () => void; onWatch: () => void }) {
  const mint = data.token.address;
  const supply = data.token.totalSupply;
  const mobile = useMedia(MOBILE_QUERY);
  const canvasApi = useRef<CanvasApi | null>(null);
  const [search, setSearch] = useSearchParams();
  const status = useMapStore((s) => s.status);
  const t = useTerminal();
  const { init, dispose, analysis, trades, selectedGroupId, selectGroup, drawer, openWallet, hoverWallet, solUsd, live: link } = t;
  const refreshLive = useMapStore((s) => s.refreshLive);
  const [tab, setTab] = useState<PanelTab>(() => (search.get('tab') as PanelTab) || 'groups');
  const [view, setView] = useState<MobileView>('map');
  const [chartOpen, setChartOpen] = useState(false);
  const [keyOpen, setKeyOpen] = useState(() => localStorage.getItem('tm.keyClosed') !== '1');
  const [locate, setLocate] = useState<string>();
  const [imgOk, setImgOk] = useState(true);
  const [copied, setCopied] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLElement>(null);
  // drag the side panel's left edge / the chart's top edge
  const side = useResizable({ key: 'tm.sideW', axis: 'x', initial: 400, min: 320, max: () => Math.min(760, (bodyRef.current?.clientWidth ?? 1200) - 420) });
  const chartH = useResizable({ key: 'tm.chartH', axis: 'y', initial: 340, min: 180, max: () => (mapRef.current?.clientHeight ?? 800) - 160 });

  // Group colours depend on how established the token is (see risk.ts);
  // set before any child paints a group.
  const maturity = useMemo(() => tokenMaturity(data.token), [data.token]);
  setRiskMaturity(maturity.level);

  /* ---------------------------------------------------------- lifecycle */
  useEffect(() => {
    init(mint);
    return () => dispose();
  }, [mint, init, dispose]);

  // deep links: ?wallet=<addr>&group=<id>
  const linked = useRef(false);
  useEffect(() => {
    if (linked.current) return;
    linked.current = true;
    const w = search.get('wallet');
    const g = search.get('group');
    if (w) openWallet(w);
    if (g) selectGroup(g);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const next = new URLSearchParams(search);
    const w = drawer.at(-1);
    if (w) next.set('wallet', w);
    else next.delete('wallet');
    if (selectedGroupId) next.set('group', selectedGroupId);
    else next.delete('group');
    next.delete('tab');
    if (next.toString() !== search.toString()) setSearch(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawer, selectedGroupId]);

  /* ---------------------------------------------------------- map overlay: groups only */
  const overlay = useMemo<MapOverlay | undefined>(() => {
    if (!analysis) return undefined;
    const colors = new Map<string, string>();
    const groupOf = new Map<string, string>();
    const badges = new Map<string, { text: string; color: string }>();
    const groups: NonNullable<MapOverlay['groups']> = [];
    for (const g of analysis.groups) {
      const r = groupRisk(g);
      for (const w of g.wallets) {
        colors.set(w, r.color);
        groupOf.set(w, g.id);
      }
      groups.push({ id: g.id, members: g.wallets, color: r.color, level: r.level, tag: r.tag, percent: g.stats.holdingPercent });
    }
    for (const [w, info] of Object.entries(analysis.wallets)) if (info.tags.includes('dev')) badges.set(w, { text: 'DEV', color: '#ffffff' });
    return {
      colors,
      groupOf,
      badges,
      groups,
      describe: (a: string) => {
        const info = analysis.wallets[a];
        if (!info) return null;
        const g = groupById(analysis, info.groupId);
        return (
          <div className="tm-tip-extra">
            {g && (
              <div style={{ color: groupTint(g) }}>
                <b>{groupTitle(g)}</b> · {g.wallets.length} linked wallets
              </div>
            )}
            {info.tags.length > 0 && <div className="faint">{info.tags.map((x) => TRADER_TAG_INFO[x].label).join(' · ')}</div>}
          </div>
        );
      },
    };
  }, [analysis, maturity.level]);

  /* ---------------------------------------------------------- live layer
     Trades replayed at their real pace (1 s clock): bubbles resize, sold-out
     wallets fade away, new big buyers appear; alerts for whales, linked
     groups dumping and exchange deposits. The snapshot itself is refreshed
     in the background every 30 s (server rebuilds when older than 90 s). */
  const feed = useLiveFeed({
    data,
    trades,
    analysis,
    solUsd,
    groupName: (id) => {
      const g = groupById(analysis, id);
      return g ? groupTitle(g) : undefined;
    },
  });
  const { live, pulses } = feed;
  // wallets that just sent tokens to an exchange carry a red badge for 10 min
  const overlayLive = useMemo(() => {
    const recent = feed.transfers.filter((x) => x.toKind === 'cex' && Date.now() / 1000 - x.ts < 600);
    if (!overlay || !recent.length) return overlay;
    const badges = new Map(overlay.badges);
    for (const x of recent) badges.set(x.from, { text: 'CEX ↗', color: '#ff4d5e' });
    return { ...overlay, badges };
  }, [overlay, feed.transfers]);
  useEffect(() => {
    const id = setInterval(() => void refreshLive(90), 30_000);
    return () => clearInterval(id);
  }, [mint, refreshLive]);

  const highlightIds = useMemo(() => {
    const g = groupById(analysis, selectedGroupId);
    if (g) return new Set(g.wallets.map((w) => `sol:${w}`));
    if (hoverWallet) return new Set([`sol:${hoverWallet}`]);
    return undefined;
  }, [analysis, selectedGroupId, hoverWallet]);

  const nodeIds = useMemo(() => new Set(data.nodes.map((n) => n.id)), [data.nodes]);
  const wantNode = locate ?? drawer.at(-1);
  const selectedNodeId = wantNode && nodeIds.has(`sol:${wantNode}`) ? `sol:${wantNode}` : undefined;
  const selGroup = groupById(analysis, selectedGroupId);
  const mapNotice =
    selGroup && !selGroup.wallets.some((w) => nodeIds.has(`sol:${w}`))
      ? `${groupTitle(selGroup)} ${
          {
            sold: 'has sold its entire position, so none of its wallets appear on the map.',
            moved: 'has transferred all of its tokens to other wallets, so none of its wallets appear on the map.',
            mixed: 'has sold or transferred its entire position, so none of its wallets appear on the map.',
            reduced: 'holds too little to appear on the map.',
            holding: 'holds too little to appear on the map.',
          }[groupPosition(selGroup).state]
        }`
      : wantNode && !selectedNodeId
        ? `${shortAddr(wantNode)} holds too little to appear on the map.`
        : undefined;
  const onSelect = (n?: BubbleNode) => {
    if (!n) return;
    if (n.kind === 'eoa' || n.kind === 'unknown' || n.origin === 'magic') openWallet(n.address);
  };

  /* ---------------------------------------------------------- numbers */
  const verdict = useMemo(() => buildVerdict(data, analysis), [data, analysis]);
  const price = trades[0]?.priceUsd ?? data.token.priceUsd;
  const mcap = price ? price * supply : data.token.marketCapUsd;
  const ch24 = data.token.priceChange24h;
  const tracing = !analysis || analysis.progress.tracing;
  const updating = data.partial;

  const showGroup = (id: string) => {
    if (selectedGroupId !== id) selectGroup(id);
    setTab('groups');
    if (mobile) setView('groups');
  };
  const download = () => canvasApi.current?.exportPng(`${data.token.symbol || 'token'}-trapmap.png`, `${data.token.symbol} · ${BRAND.name}`);
  const copyAddress = () => {
    void navigator.clipboard.writeText(mint);
    setCopied(true);
    setTimeout(() => setCopied(false), 1300);
  };
  const toggleKey = () =>
    setKeyOpen((o) => {
      localStorage.setItem('tm.keyClosed', o ? '1' : '0');
      return !o;
    });

  const panel = (
    <>
      {tab === 'groups' && <GroupList onShowOnMap={mobile ? () => setView('map') : undefined} />}
      {tab === 'holders' && <HolderList />}
      {tab === 'trades' && <TradeList />}
    </>
  );

  return (
    <div className={`tv ${mobile ? 'is-mobile' : ''}`}>
      {/* ------------------------------------------------ token bar */}
      <div className="tv-bar">
        <div className="tv-token">
          {data.token.logoUrl && imgOk ? (
            <img className="tv-logo" src={data.token.logoUrl} alt="" onError={() => setImgOk(false)} />
          ) : (
            <div className="tv-logo ph">{(data.token.symbol || '?').slice(0, 1).toUpperCase()}</div>
          )}
          <div className="tv-names">
            <div className="tv-sym">{data.token.symbol}</div>
            <button className="tv-addr" onClick={copyAddress} title="Copy token address">
              {copied ? 'Copied' : shortAddr(mint, 4, 4)} <IconCopy size={13} />
            </button>
          </div>
        </div>

        <div className="tv-stats">
          <div>
            <em>Price</em>
            <b className="mono">{fmtPrice(price)}</b>
          </div>
          <div>
            <em>Market cap</em>
            <b className="mono">{fmtUsd(mcap)}</b>
          </div>
          {ch24 !== undefined && (
            <div className="hide-sm">
              <em>24h</em>
              <b className={`mono ${ch24 >= 0 ? 'buy' : 'sell'}`}>{`${ch24 >= 0 ? '+' : ''}${ch24.toFixed(1)}%`}</b>
            </div>
          )}
          {data.token.liquidityUsd !== undefined && (
            <div className="hide-sm">
              <em>Liquidity</em>
              <b className="mono">{fmtUsd(data.token.liquidityUsd)}</b>
            </div>
          )}
        </div>

        <LiveChip link={link} />

        <div className="tv-actions">
          {!mobile && (
            <button className={`tm-btn ${chartOpen ? 'on' : ''}`} onClick={() => setChartOpen((o) => !o)} aria-pressed={chartOpen}>
              <IconChart /> {chartOpen ? 'Hide chart' : 'Price chart'}
            </button>
          )}
          <button className="tm-btn" onClick={download} title="Save the map as a PNG image">
            <IconDownload /> <span className="lbl">Download image</span>
          </button>
          <button className="tm-btn" onClick={onShare} title="Copy a link to this map">
            <IconLink /> <span className="lbl">Copy link</span>
          </button>
          <button className="tm-btn icon" onClick={onWatch} title="Add to watchlist" aria-label="Add to watchlist">
            <IconStar />
          </button>
        </div>
      </div>

      <div className={`tv-body ${side.dragging || chartH.dragging ? 'is-resizing' : ''}`} ref={bodyRef}>
        {/* ------------------------------------------------ map */}
        <section ref={mapRef} className={`tv-map ${mobile && view !== 'map' ? 'm-hidden' : ''} ${chartOpen ? 'with-chart' : ''}`} aria-label="Bubble map">
          <div className="tv-canvas">
            <BubbleCanvas
              data={data}
              groupsOnly
              overlay={overlayLive}
              pulses={pulses}
              live={live}
              selectedId={selectedNodeId}
              highlightIds={highlightIds}
              focusMode
              showLabels
              onSelect={onSelect}
              onReady={(a) => (canvasApi.current = a)}
            />

            <div className="tv-map-top">
              {mobile && (
                <button className={`tv-verdict-pill lvl-${verdict.level ?? 'unknown'}`} onClick={() => setView('groups')}>
                  {verdict.level && <b>{LEVEL_LABEL[verdict.level]}</b>}
                  <span>{verdict.headline}</span>
                </button>
              )}
              {(updating || tracing) && (
                <span className="tv-status">
                  <span className="spinner" />
                  {updating
                    ? `Linking wallets${status?.state === 'indexing' ? ` ${Math.round((status.progress ?? 0) * 100)}%` : '…'}`
                    : analysis?.progress.toTrace
                      ? `Finding linked wallets ${analysis.progress.traced}/${analysis.progress.toTrace}`
                      : 'Finding linked wallets…'}
                </span>
              )}
              {selGroup && (
                <span className="tv-filter" style={{ ['--g' as string]: groupTint(selGroup) }}>
                  <i />
                  {groupTitle(selGroup)} · {pct(selGroup.stats.holdingPercent)}
                  <button onClick={() => selectGroup(undefined)} aria-label="Clear group">
                    <IconClose size={14} />
                  </button>
                </span>
              )}
            </div>

            {mapNotice && <div className="tv-notice">{mapNotice}</div>}

            <LiveAlerts
              alerts={feed.alerts}
              compact={mobile}
              onDismiss={feed.dismiss}
              onOpen={(a) => {
                if (a.groupId) showGroup(a.groupId);
                if (a.wallet) openWallet(a.wallet);
              }}
            />

            {keyOpen ? (
              <div className="tv-key" role="note">
                <span>
                  <i className="k-size" /> Size = % held
                </span>
                <span>
                  <i className="k-high" /> Red = flagged group
                </span>
                <span>
                  <i className="k-med" /> Amber = suspicious
                </span>
                <span>
                  <i className="k-solo" /> Soft colours = independent
                </span>
                <span>
                  <i className="k-line" /> Line = money moved
                </span>
                <span>
                  <i className="k-ring" /> Dashed = funding wallet
                </span>
                <button onClick={toggleKey} aria-label="Hide map key">
                  <IconClose size={14} />
                </button>
              </div>
            ) : (
              <button className="tv-key-btn" onClick={toggleKey}>
                <IconInfo size={16} /> How to read the map
              </button>
            )}

            <div className="tv-zoom" role="group" aria-label="Zoom">
              <button onClick={() => canvasApi.current?.zoomBy(1.35)} aria-label="Zoom in">
                <IconPlus />
              </button>
              <button onClick={() => canvasApi.current?.zoomBy(1 / 1.35)} aria-label="Zoom out">
                <IconMinus />
              </button>
              <button onClick={() => canvasApi.current?.fit()} aria-label="Fit map">
                <IconFit />
              </button>
            </div>
          </div>

          {chartOpen && !mobile && (
            <div className="tv-chart" style={{ flexBasis: chartH.size }}>
              <div className={`tm-split h ${chartH.dragging ? 'on' : ''}`} aria-label="Resize chart" title="Drag to resize · double-click to reset" {...chartH.handleProps}>
                <i />
              </div>
              <div className="tv-chart-head">
                <b>Price chart</b>
                <span className="faint">Coloured markers show when linked groups bought and sold</span>
                <button className="tm-iconbtn" onClick={() => setChartOpen(false)} aria-label="Close chart">
                  <IconClose />
                </button>
              </div>
              <div className="tv-chart-body">
                <Suspense fallback={<div className="tm-empty">Loading chart…</div>}>
                  <PriceChart mint={mint} supply={supply} />
                </Suspense>
              </div>
            </div>
          )}
        </section>

        {/* ------------------------------------------------ side panel / mobile views */}
        <aside className={`tv-side ${mobile && view === 'map' ? 'm-hidden' : ''}`} style={mobile ? undefined : { width: side.size, flexBasis: side.size }}>
          {!mobile && (
            <div className={`tm-split v ${side.dragging ? 'on' : ''}`} aria-label="Resize side panel" title="Drag to resize · double-click to reset" {...side.handleProps}>
              <i />
            </div>
          )}
          {(!mobile || view === 'groups') && <VerdictCard v={verdict} onGroup={showGroup} />}
          {!mobile && (
            <div className="tm-tabs" role="tablist">
              {(['groups', 'holders', 'trades'] as PanelTab[]).map((x) => (
                <button key={x} role="tab" aria-selected={tab === x} className={tab === x ? 'on' : ''} onClick={() => setTab(x)}>
                  {x === 'groups' ? `Linked groups${analysis ? ` (${analysis.groups.length})` : ''}` : x === 'holders' ? 'Top holders' : 'Live trades'}
                </button>
              ))}
            </div>
          )}
          <div className="tv-side-body">{mobile ? (view === 'map' ? null : view === 'groups' ? <GroupList onShowOnMap={() => setView('map')} /> : view === 'holders' ? <HolderList /> : <TradeList />) : panel}</div>
        </aside>

        {drawer.length > 0 && (
          <WalletDrawer
            mint={mint}
            supply={supply}
            onLocate={(a) => {
              setLocate(a);
              if (mobile) setView('map');
              setTimeout(() => setLocate(undefined), 4000);
            }}
          />
        )}
      </div>

      {mobile && (
        <nav className="tv-mnav" aria-label="Sections">
          {(
            [
              ['map', 'Map', IconMap],
              ['groups', 'Risk', IconBolt],
              ['holders', 'Holders', IconUsers],
              ['trades', 'Trades', IconList],
            ] as const
          ).map(([k, label, Icon]) => (
            <button key={k} className={view === k ? 'on' : ''} onClick={() => setView(k)} aria-current={view === k}>
              <Icon size={20} />
              {label}
              {k === 'groups' && verdict.level && verdict.level !== 'low' && <i className={`tv-mnav-dot lvl-${verdict.level}`} />}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}

const POLL_WHY: Record<string, string> = {
  capacity: 'All live streams are in use right now',
  budget: 'Daily streaming budget reached',
  token_budget: 'This token is very busy; streaming paused for this hour',
  connecting: 'Connecting the live stream…',
  off: 'Live streaming is off',
};

/** How fresh the trades on screen are: streamed (~1-2 s) or polled. */
function LiveChip({ link }: { link: LiveLink }) {
  const streaming = link.connected && link.mode === 'stream';
  const why = !link.connected ? 'Reconnecting…' : (link.reason && (POLL_WHY[link.reason] ?? 'Live stream unavailable')) || '';
  return (
    <span
      className={`tv-live ${streaming ? 'on' : 'poll'}`}
      title={streaming ? 'Streaming: trades appear 1–2 seconds after they happen on-chain' : `${why}. Trades update every few seconds instead.`}
    >
      <i />
      {streaming ? 'Live' : 'Live · delayed'}
    </span>
  );
}
